import React, { ReactElement, ReactNode } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { cssTheme } from '../../../themes';
import { getLinariaClassName } from '../../../utils/getLinariaClassName';
import { Button } from '../Button';
import { Grid } from '../helpers/Grid';
import { Icon } from '../Icon';
export const ALERT_VARIANTS = Object.keys(cssTheme.colors.alert) as Array<keyof typeof cssTheme.colors.alert>;
export type AlertVariant = (typeof ALERT_VARIANTS)[number];
export type IconAlignment = 'flex-start' | 'center';
interface Props {
  /** An icon placed before the alert content. It can be any type of React Element */
  icon?: ReactElement;
  /** The content of the Alert, a React component or pure text */
  children: ReactNode;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** Pass through alignIcon to allow vertically center aligned icon for example */
  alignIcon?: IconAlignment;
  /** Title string of the Alert */
  title?: string | ReactNode;
  /** Passing this callback will show the close button.
   * Pressing the button will cause the callback execution */
  onClose?: () => void;
  /** Color schemes are based on pre-defined themes */
  variant?: AlertVariant;
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string;
  /** Style prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledAlert = styled(Alert)` */
  style?: React.CSSProperties;
}
export const AlertWrapper = /*#__PURE__*/styled('div')({
  name: "AlertWrapper",
  class: "a14nskwh",
  propsAsIs: false
});
export const ContentWrapper = /*#__PURE__*/styled('div')({
  name: "ContentWrapper",
  class: "c19ei4a3",
  propsAsIs: false
});
export const Title = /*#__PURE__*/styled('div')({
  name: "Title",
  class: "tq2nyim",
  propsAsIs: false
});
export const Description = /*#__PURE__*/styled('div')({
  name: "Description",
  class: "d20gq08",
  propsAsIs: false
});
const _exp15 = /*#__PURE__*/() => Grid;
const _exp16 = /*#__PURE__*/() => ({
  alignIcon
}) => alignIcon;
export const IconGrid = /*#__PURE__*/styled(_exp15())({
  name: "IconGrid",
  class: "i130104x",
  propsAsIs: true,
  vars: {
    "i130104x-0": [_exp16()]
  }
});
export const IconWrapper = /*#__PURE__*/styled('div')({
  name: "IconWrapper",
  class: "io1cs8y",
  propsAsIs: false
});
export const ButtonWrapper = /*#__PURE__*/styled('div')({
  name: "ButtonWrapper",
  class: "b1hhbx7u",
  propsAsIs: false
});
export const Alert = ({
  icon,
  onClose,
  children,
  className,
  alignIcon = 'flex-start',
  variant = 'default',
  title,
  dataId = '',
  style
}: Props) => {
  const handleCloseButtonClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    onClose?.();
  };
  const hasTitle = Boolean(title);
  const hasIcon = Boolean(icon);
  const hasTitleAndIcon = hasTitle && hasIcon;
  return <AlertWrapper className={cx(`${getLinariaClassName(AlertWrapper)}--variant-${variant}`, className)} data-id={dataId} style={style}>
      <Grid container spacing="s000" wrap="nowrap">
        {icon && <IconGrid mobileSm="fit" alignIcon={alignIcon} className={cx(Boolean(title) && '--has-title')}>
            <IconWrapper className={`${getLinariaClassName(IconWrapper)}--variant-${variant}`}>
              {icon}
            </IconWrapper>
          </IconGrid>}
        <Grid mobileSm="fill">
          <ContentWrapper className={`${getLinariaClassName(ContentWrapper)}--variant-${variant}`}>
            {Boolean(title) && <Title data-id="AlertTitle" className={`${getLinariaClassName(Title)}--variant-${variant}`}>
                {title}
              </Title>}
            {!title && <Description className={cx(`${getLinariaClassName(Description)}--variant-${variant}`, hasTitleAndIcon ? '--has-title-and-icon' : hasTitle ? '--has-title' : '')}>
                {children}
              </Description>}
          </ContentWrapper>
        </Grid>

        {Boolean(onClose) && <ButtonWrapper>
            <Button hasTouchArea variant="transparent" size="md" iconStart={<Icon name="Close" size="sm" colorPath={`alert.${variant}.icon`} />} onClick={handleCloseButtonClick} />
          </ButtonWrapper>}
      </Grid>
      {Boolean(title) && <Description className={cx(`${getLinariaClassName(Description)}--variant-${variant}`, hasTitleAndIcon ? '--has-title-and-icon' : hasTitle ? '--has-title' : '')}>
          {children}
        </Description>}
    </AlertWrapper>;
};

require("./Alert.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Alert.tsx");